// ════════════════════════════════════════════════════════════════════════
//  Übungscodes einer Klasse
//
//  Die Abfragen verlassen sich auf RLS: die Policies filtern auf die eigenen
//  Klassen. Das hier zusätzlich zu filtern würde nur verschleiern, wo die Grenze
//  wirklich gezogen wird.
// ════════════════════════════════════════════════════════════════════════

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { makeCodes } from "@otto/lehrgang";
import { queryKeys, type OttoClient } from "@otto/plattform";

export type Code = {
  readonly id: string;
  readonly code: string;
  /**
   * Hat unter diesem Code schon jemand geübt?
   *
   * Entscheidet, ob er sich löschen lässt. Gezählt wird, nicht gelesen: die
   * Codeseite braucht die Zahl der Übungen nicht und soll sie auch nicht
   * bekommen – sie zeigt Kärtchen, keine Ergebnisse.
   */
  readonly used: boolean;
};

export function useCodes(client: OttoClient, classId: string) {
  return useQuery({
    queryKey: queryKeys.codes(classId),
    queryFn: async (): Promise<Code[]> => {
      // `progress(count)` und `test_results(count)`: PostgREST zählt die
      // verbundenen Zeilen und schickt nur die Zahl. Beide zusammen, weil ein
      // Kind, das nur den Tempotest getippt hat, ebenso geübt hat.
      const { data, error } = await client
        .from("students")
        .select("id, code, progress(count), test_results(count)")
        .eq("class_id", classId)
        .order("code");
      if (error) throw error;

      return (data ?? []).map((row) => {
        const zaehlung = (feld: unknown) =>
          Array.isArray(feld) ? ((feld[0] as { count?: number })?.count ?? 0) : 0;
        return {
          id: row.id,
          code: row.code,
          used: zaehlung(row.progress) + zaehlung(row.test_results) > 0,
        };
      });
    },
  });
}

/**
 * Erzeugt Codes und legt sie an.
 *
 * Die bereits vergebenen Codes der Klasse werden mitgegeben, damit ein
 * Klassensatz nicht an einer Dublette scheitert. Die Datenbank hat zusätzlich
 * ein unique auf `students.code` – gegen Kollisionen mit ANDEREN Schulen hilft
 * nur die; deshalb wird ein Konflikt hier abgefangen und der Satz erneut
 * versucht, statt der Lehrkraft eine Fehlermeldung zu zeigen.
 */
export function useMakeCodes(client: OttoClient, classId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { count: number; taken: readonly string[] }) => {
      let taken = [...input.taken];
      for (let attempt = 0; attempt < 3; attempt++) {
        const neue = makeCodes(input.count, { taken });
        const { error } = await client
          .from("students")
          .insert(neue.map((code) => ({ class_id: classId, code })));
        if (!error) return neue;
        // 23505 = unique_violation: ein Code ist anderswo schon vergeben.
        if (error.code !== "23505") throw error;
        taken = [...taken, ...neue];
      }
      throw new Error(
        "Es ließen sich keine freien Codes finden. Bitte erneut versuchen.",
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.codes(classId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.results(classId) });
    },
  });
}

/**
 * Löscht einen einzelnen Code. Der Fortschritt geht per Kaskade mit – das ist
 * gewollt: „Kind verlässt die Klasse“ oder „Eltern widersprechen“ muss sofort
 * und vollständig wirken.
 */
export function useDeleteCode(client: OttoClient, classId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (studentId: string) => {
      const { error } = await client.from("students").delete().eq("id", studentId);
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.codes(classId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.results(classId) });
    },
  });
}
